import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
    Container,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    Paper,
    CircularProgress,
    InputAdornment,
    IconButton
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import api from '../services/api';
import LottieLoading from '../components/common/LottieLoading';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const auth = useContext(AuthContext);
    const navigate = useNavigate();

    // Customization state
    const [customization, setCustomization] = useState({
        logoUrl: '',
        backgroundImageUrl: '',
        primaryColor: '#667eea',
        secondaryColor: '#764ba2',
        orgName: ''
    });
    const [isSubdomain, setIsSubdomain] = useState(false);

    useEffect(() => {
        const checkSubdomain = async () => {
            const hostname = window.location.hostname;
            const baseDomain = import.meta.env.VITE_BASE_DOMAIN;
            let subdomain = '';

            // 1. Try VITE_BASE_DOMAIN match
            if (baseDomain && hostname.endsWith(`.${baseDomain}`)) {
                subdomain = hostname.replace(`.${baseDomain}`, '');
            }
            // 2. Try localhost (e.g., tenant.localhost)
            else if (hostname.endsWith('.localhost') && hostname !== 'localhost') {
                subdomain = hostname.replace('.localhost', '');
            }
            // 3. Generic fallback: 3+ parts (e.g. tenant.domain.com) and not an IP
            else if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
                const parts = hostname.split('.');
                if (parts.length >= 3) {
                    subdomain = parts[0];
                }
            }

            // Fetch organization if valid subdomain found
            if (subdomain && subdomain !== 'www') {
                try {
                    console.log('Detected subdomain:', subdomain);
                    const res = await api.get(`/api/organizations/public/by-subdomain/${subdomain}`);
                    const org = res.data;

                    setCustomization({
                        logoUrl: org.loginScreen?.logoUrl || org.logoUrl || '',
                        backgroundImageUrl: org.loginScreen?.backgroundImageUrl || '',
                        primaryColor: org.loginScreen?.primaryColor || org.theme?.primaryColor || '#667eea',
                        secondaryColor: org.loginScreen?.secondaryColor || org.theme?.secondaryColor || '#764ba2',
                        orgName: org.name
                    });
                    setIsSubdomain(true);
                } catch (err) {
                    console.error('Organization not found for subdomain:', subdomain);
                    // Fallback to default
                }
            }
        };

        checkSubdomain();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Prevent double submission
        if (loading) return;

        setLoading(true);
        setError('');

        try {
            const data = await auth?.login(email, password);
            if (data?.user?.role === 'learner') {
                navigate('/learner/dashboard');
            } else {
                navigate('/dashboard');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Échec de la connexion');
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: customization.backgroundImageUrl
                    ? `url(${customization.backgroundImageUrl})`
                    : `linear-gradient(135deg, ${customization.primaryColor} 0%, ${customization.secondaryColor} 100%)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                padding: 2,
                position: 'relative'
            }}
        >
            {/* Lottie Loading Overlay */}
            {loading && <LottieLoading message="Connexion en cours..." animationType="learner" />}

            {/* Overlay for background image readability */}
            {customization.backgroundImageUrl && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 0
                }} />
            )}

            <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
                <Paper
                    elevation={24}
                    sx={{
                        padding: { xs: 3, sm: 5 },
                        borderRadius: 4,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)'
                    }}
                >
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${customization.primaryColor} 0%, ${customization.secondaryColor} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                boxShadow: `0 8px 16px ${customization.primaryColor}40`,
                                overflow: 'hidden'
                            }}
                        >
                            {customization.logoUrl ? (
                                <img src={customization.logoUrl} alt="Logo" style={{ width: '60%', height: '60%', objectFit: 'contain' }} />
                            ) : (
                                <LoginIcon sx={{ fontSize: 40, color: 'white' }} />
                            )}
                        </Box>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                background: `linear-gradient(135deg, ${customization.primaryColor} 0%, ${customization.secondaryColor} 100%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 1
                            }}
                        >
                            {isSubdomain ? `Bienvenue sur ${customization.orgName}` : 'Bienvenue'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Connectez-vous à votre compte
                        </Typography>
                    </Box>

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Adresse email"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&:hover fieldset': {
                                        borderColor: customization.primaryColor,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: customization.primaryColor,
                                    }
                                }
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Mot de passe"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            disabled={loading}
                                            aria-label="Modifier la visibilité du mot de passe"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&:hover fieldset': {
                                        borderColor: customization.primaryColor,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: customization.primaryColor,
                                    }
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Link
                                to="/forgot-password"
                                style={{
                                    textDecoration: 'none',
                                    color: customization.primaryColor,
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                }}
                            >
                                Mot de passe oublié ?
                            </Link>
                        </Box>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                                mt: 4,
                                mb: 2,
                                py: 1.5,
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${customization.primaryColor} 0%, ${customization.secondaryColor} 100%)`,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: `0 4px 12px ${customization.primaryColor}40`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${customization.primaryColor}dd 0%, ${customization.secondaryColor}dd 100%)`,
                                    boxShadow: `0 6px 16px ${customization.primaryColor}60`,
                                },
                                '&:disabled': {
                                    background: 'linear-gradient(135deg, #999 0%, #888 100%)',
                                }
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: 'white' }} />
                            ) : (
                                'Se connecter'
                            )}
                        </Button>
                        {/*<Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Link
                                to="/register"
                                style={{
                                    textDecoration: 'none',
                                    color: customization.primaryColor,
                                    fontWeight: 500
                                }}
                            >
                                Vous n'avez pas de compte ? Inscrivez-vous
                            </Link>
                        </Box>*/}
                    </Box>
                </Paper>
                <Box sx={{ mt: 4, textAlign: 'center', opacity: 0.8 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Link to="/of-cgu" style={{ color: 'white', textDecoration: 'none', fontSize: '0.8rem' }}>CGU</Link>
                        <Typography variant="caption" sx={{ color: 'white' }}>•</Typography>
                        <Link to="/rgpd" style={{ color: 'white', textDecoration: 'none', fontSize: '0.8rem' }}>RGPD</Link>
                        <Typography variant="caption" sx={{ color: 'white' }}>•</Typography>
                        <Link to="/learner-rules" style={{ color: 'white', textDecoration: 'none', fontSize: '0.8rem' }}>Règlement Apprenant</Link>
                        <Typography variant="caption" sx={{ color: 'white' }}>•</Typography>
                        <Link to="/rse" style={{ color: 'white', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 500 }}>Politique RSE</Link>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'white', display: 'block', mt: 1 }}>
                        © 2025 Edu-JALM. Tous droits réservés.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Login;
